import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useMap } from 'react-leaflet';
import { matchPostcode, extractPostcode } from '../utils/searchUtils';

const RECENT_SEARCHES_KEY = 'skipmap_recent_searches';
const MAX_RECENT_SEARCHES = 5;

const SearchBar = ({ markers, setMapInstance, onSearchResults, isMobile }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isResultsVisible, setIsResultsVisible] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);  // Keyboard navigation index
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const searchContainerRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const inputRef = useRef(null);
  const map = useMap();

  // Load recent searches from localStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (stored) {
        setRecentSearches(JSON.parse(stored));
      }
    } catch (e) {
      console.warn('Failed to load recent searches:', e);
    }
  }, []);

  // Save a search term to recent searches
  const saveRecentSearch = (term) => {
    if (!term || term.trim().length < 2) return;

    const trimmed = term.trim();
    const updated = [trimmed, ...recentSearches.filter(s => s.toLowerCase() !== trimmed.toLowerCase())]
      .slice(0, MAX_RECENT_SEARCHES);

    setRecentSearches(updated);
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
    } catch (e) {
      console.warn('Failed to save recent searches:', e);
    }
  };

  // Clear recent searches
  const clearRecentSearches = () => {
    setRecentSearches([]);
    try {
      localStorage.removeItem(RECENT_SEARCHES_KEY);
    } catch (e) {
      console.warn('Failed to clear recent searches:', e);
    }
  };

  // Collapse mobile search when clicking outside
  const handleClickOutside = useCallback((event) => {
    if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
      setIsResultsVisible(false);
      if (isMobile && !searchTerm) {
        setMobileExpanded(false);
      }
    }
  }, [isMobile, searchTerm]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Function to highlight matching text in search results
  const highlightMatch = (text, term) => {
    if (!text || !term) return text;

    const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <mark key={index} style={{ backgroundColor: '#FEF08A', padding: '0 2px', borderRadius: '2px' }}>
          {part}
        </mark>
      ) : part
    );
  };

  // Simple function to extract street name from address
  const extractStreetName = (address) => {
    if (!address) return "Unknown Street";

    // Remove any house/building numbers at the beginning
    const withoutNumbers = address.replace(/^[\d\s-/,]+/, '').trim();

    // Extract first part which is likely the street name
    const parts = withoutNumbers.split(',');
    return parts[0].trim();
  };

  // Group markers by street name
  const groupByStreet = (markers) => {
    const streets = {};

    markers.forEach(marker => {
      if (!marker.streetAddress) return;

      const streetName = extractStreetName(marker.streetAddress);

      if (!streets[streetName]) {
        streets[streetName] = [];
      }

      streets[streetName].push(marker);
    });

    return Object.entries(streets).map(([street, markers]) => ({
      street,
      markers
    }));
  };

  // Handle search input changes with debouncing
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Clear existing debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (value.trim() === '') {
      setSearchResults([]);
      setIsResultsVisible(false);
      if (onSearchResults) {
        onSearchResults([]);
      }
      return;
    }

    // Debounce search for 300ms
    debounceTimerRef.current = setTimeout(() => {
      const filteredResults = markers.filter(marker => {
        if (marker.status !== 'delivered') {
          return false;
        }

        if (!marker.streetAddress && !marker.formattedAddress) return false;

        const streetMatch = marker.streetAddress && marker.streetAddress.toLowerCase().includes(value.toLowerCase());
        const formattedMatch = marker.formattedAddress && marker.formattedAddress.toLowerCase().includes(value.toLowerCase());
        const addressMatch = streetMatch || formattedMatch;

        const businessNameMatch = marker.businessName && marker.businessName.toLowerCase().includes(value.toLowerCase());

        const directPostcodeMatch = marker.postcode && matchPostcode(marker.postcode, value);

        let extractedPostcodeMatch = false;
        if (!directPostcodeMatch) {
          const extractedFromStreet = extractPostcode(marker.streetAddress);
          const extractedFromFormatted = !extractedFromStreet ? extractPostcode(marker.formattedAddress) : null;
          const extractedPostcode = extractedFromStreet || extractedFromFormatted;
          extractedPostcodeMatch = extractedPostcode && matchPostcode(extractedPostcode, value);
        }

        return addressMatch || businessNameMatch || directPostcodeMatch || extractedPostcodeMatch;
      });

      const groupedResults = groupByStreet(filteredResults);

      setSearchResults(groupedResults);
      setIsResultsVisible(true);

      if (onSearchResults) {
        onSearchResults(filteredResults);
      }

      if (filteredResults.length === 1) {
        const marker = filteredResults[0];
        map.setView(marker.position, 16);
      } else if (filteredResults.length > 1) {
        const bounds = filteredResults.map(m => m.position);
        map.fitBounds(bounds, { padding: [50, 50] });
      }
    }, 300);
  };

  // Handle selection of a search result
  const handleSelectResult = (marker) => {
    saveRecentSearch(searchTerm);

    map.setView(marker.position, 16);

    map.eachLayer((layer) => {
      if (layer._latlng &&
          layer._latlng.lat === marker.position[0] &&
          layer._latlng.lng === marker.position[1]) {
        layer.openPopup();
      }
    });

    setIsResultsVisible(false);
    setSearchTerm('');
    if (isMobile) {
      setMobileExpanded(false);
    }
  };

  // Handle clicking a recent search
  const handleRecentSearchClick = (term) => {
    setSearchTerm(term);
    handleSearchChange({ target: { value: term } });
  };

  // Handle input focus - show recent searches if empty
  const handleInputFocus = () => {
    if (!searchTerm.trim() && recentSearches.length > 0) {
      setIsResultsVisible(true);
    }
  };

  // Get flat list of all selectable items for keyboard navigation
  const getFlatItems = () => {
    if (!searchTerm && recentSearches.length > 0) {
      return recentSearches.map(term => ({ type: 'recent', term }));
    }
    const items = [];
    searchResults.forEach(group => {
      group.markers.forEach(marker => {
        items.push({ type: 'result', marker });
      });
    });
    return items;
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    const items = getFlatItems();
    const maxIndex = items.length - 1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => (prev < maxIndex ? prev + 1 : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : maxIndex));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex <= maxIndex) {
          const selected = items[selectedIndex];
          if (selected.type === 'recent') {
            handleRecentSearchClick(selected.term);
          } else {
            handleSelectResult(selected.marker);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsResultsVisible(false);
        setSelectedIndex(-1);
        inputRef.current?.blur();
        if (isMobile) {
          setMobileExpanded(false);
        }
        break;
      default:
        break;
    }
  };

  // Reset selected index when results change
  useEffect(() => {
    setSelectedIndex(-1);
  }, [searchResults, recentSearches, searchTerm]);

  // Auto-focus input when mobile search expands
  useEffect(() => {
    if (isMobile && mobileExpanded && inputRef.current) {
      // Small delay to allow the DOM to update before focusing
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isMobile, mobileExpanded]);

  // Handle closing mobile search
  const handleMobileClose = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setSearchTerm('');
    setSearchResults([]);
    setIsResultsVisible(false);
    setMobileExpanded(false);
    if (onSearchResults) {
      onSearchResults([]);
    }
  };

  // --- Mobile collapsed: just the icon button ---
  if (isMobile && !mobileExpanded) {
    return (
      <button
        onClick={() => setMobileExpanded(true)}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1001,
          width: '40px',
          height: '40px',
          borderRadius: '8px',
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          border: '1px solid #E7E5E4',
          boxShadow: '0 2px 8px rgba(28, 25, 23, 0.12)',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '18px',
          padding: 0
        }}
      >
        🔍
      </button>
    );
  }

  // --- Mobile expanded or Desktop: full search bar ---
  return (
    <div
      ref={searchContainerRef}
      style={{
        position: 'absolute',
        top: '10px',
        left: isMobile ? '10px' : '50%',
        right: isMobile ? '10px' : 'auto',
        transform: isMobile ? 'none' : 'translateX(-50%)',
        zIndex: 1001,
        width: isMobile ? 'auto' : '90%',
        maxWidth: isMobile ? 'none' : '400px'
      }}
    >
      <div style={{
        display: 'flex',
        position: 'relative'
      }}>
        <input
          ref={inputRef}
          type="text"
          placeholder="Search address, postcode, or business name..."
          value={searchTerm}
          onChange={handleSearchChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          style={{
            width: '100%',
            padding: '8px 12px',
            paddingLeft: '30px',
            paddingRight: isMobile ? '40px' : '12px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            fontSize: '14px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        />
        {/* Search icon */}
        <div style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          color: '#666'
        }}>
          🔍
        </div>

        {/* Close/Clear button */}
        {isMobile ? (
          <button
            onClick={handleMobileClose}
            style={{
              position: 'absolute',
              right: '8px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'none',
              border: 'none',
              color: '#666',
              cursor: 'pointer',
              fontSize: '16px',
              padding: '4px'
            }}
          >
            ✕
          </button>
        ) : (
          searchTerm && (
            <button
              onClick={() => {
                if (debounceTimerRef.current) {
                  clearTimeout(debounceTimerRef.current);
                }
                setSearchTerm('');
                setSearchResults([]);
                setIsResultsVisible(false);
                if (onSearchResults) {
                  onSearchResults([]);
                }
              }}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '16px'
              }}
            >
              ✕
            </button>
          )
        )}
      </div>

      {/* Search results dropdown */}
      {isResultsVisible && searchResults.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          maxHeight: '300px',
          overflowY: 'auto',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1002
        }}>
          {(() => {
            let flatIndex = 0;
            return searchResults.map((group, groupIndex) => (
              <div key={groupIndex}>
                {/* Street name header */}
                <div style={{
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  fontWeight: 'bold',
                  borderBottom: '1px solid #eee'
                }}>
                  {group.street} ({group.markers.length})
                </div>

                {/* Individual addresses */}
                {group.markers.map((marker, markerIndex) => {
                  const postcode = marker.postcode || extractPostcode(marker.streetAddress) || extractPostcode(marker.formattedAddress);
                  const businessName = marker.businessName;
                  const currentIndex = flatIndex++;
                  const isSelected = currentIndex === selectedIndex;

                  return (
                    <div
                      key={`${groupIndex}-${markerIndex}`}
                      onClick={() => handleSelectResult(marker)}
                      style={{
                        padding: '8px 12px',
                        borderBottom: '1px solid #eee',
                        cursor: 'pointer',
                        fontSize: '14px',
                        backgroundColor: isSelected ? '#E0E7FF' : 'transparent'
                      }}
                      onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                      onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      {businessName && (
                        <span style={{ fontWeight: 'bold' }}>
                          {highlightMatch(businessName, searchTerm)}
                          <span style={{ fontWeight: 'normal' }}>, </span>
                        </span>
                      )}
                      {highlightMatch(marker.streetAddress, searchTerm)}
                      {postcode && (
                        <span style={{
                          fontWeight: 'bold',
                          marginLeft: '6px',
                          color: '#3B82F6'
                        }}>
                          ({highlightMatch(postcode, searchTerm)})
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>
      )}

      {/* No results message */}
      {isResultsVisible && searchTerm && searchResults.length === 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          padding: '12px',
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1002,
          textAlign: 'center',
          color: '#666'
        }}>
          No matching addresses found
        </div>
      )}

      {/* Recent searches dropdown */}
      {isResultsVisible && !searchTerm && recentSearches.length > 0 && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          backgroundColor: 'white',
          border: '1px solid #ccc',
          borderTop: 'none',
          borderRadius: '0 0 4px 4px',
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
          zIndex: 1002
        }}>
          {/* Header */}
          <div style={{
            padding: '8px 12px',
            backgroundColor: '#f5f5f5',
            fontWeight: 'bold',
            borderBottom: '1px solid #eee',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>Recent Searches</span>
            <button
              onClick={clearRecentSearches}
              style={{
                background: 'none',
                border: 'none',
                color: '#666',
                cursor: 'pointer',
                fontSize: '12px',
                textDecoration: 'underline'
              }}
            >
              Clear
            </button>
          </div>

          {/* Recent search items */}
          {recentSearches.map((term, index) => {
            const isSelected = index === selectedIndex;
            return (
              <div
                key={index}
                onClick={() => handleRecentSearchClick(term)}
                style={{
                  padding: '8px 12px',
                  borderBottom: '1px solid #eee',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  backgroundColor: isSelected ? '#E0E7FF' : 'transparent'
                }}
                onMouseOver={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = '#f0f0f0'; }}
                onMouseOut={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = 'transparent'; }}
              >
                <span style={{ color: '#999' }}>🕐</span>
                {term}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
